import type { Task } from '../../../types';
import CompletionCheckbox from '../../../components/shared/CompletionCheckbox';
import ActionMenu, {
  createEditAction,
  createDeleteAction,
  createDuplicateAction,
} from '../../../components/shared/ActionMenu';
import { AppIcon } from '../../../components/icons/AppIcon';
import { formatTaskDate, getTaskDueBucket, type TaskBucket } from '../lib/taskBuckets';
import { useUsers } from '../../../contexts/users/UsersContext';
import { cn } from '@/lib/utils';

interface TaskCardProps {
  task: Task;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onToggleCompletion: (id: string) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  onDuplicate: (task: Task) => void;
}

const TYPE_LABEL: Record<string, string> = {
  agenda: 'Agendamento',
  agendamento: 'Agendamento',
  call: 'Ligação',
  ligacao: 'Ligação',
  follow: 'Follow-up',
  'follow-up': 'Follow-up',
  follow_up: 'Follow-up',
  email: 'E-mail',
  'e-mail': 'E-mail',
};

const CARD_TONE: Record<TaskBucket, string> = {
  overdue: 'border-error/35 bg-danger-bg',
  today: 'border-warning/35 bg-warning-bg',
  done: 'border-border bg-surface',
  week: 'border-border bg-surface',
  later: 'border-border bg-surface',
};

const DATE_TONE: Record<TaskBucket, string> = {
  overdue: 'text-error',
  today: 'text-warning',
  done: 'text-muted',
  week: 'text-success',
  later: 'text-success',
};

const SELECT_CLASS = 'size-4 shrink-0 rounded-xs border-border bg-card text-primary accent-primary';

function typeLabel(type?: string) {
  if (!type) return 'Tarefa';
  return TYPE_LABEL[type.toLowerCase()] ?? type;
}

function assigneeLabel(
  assignedTo: Task['assignedTo'] | undefined,
  users: { id: string; name: string }[],
): string | null {
  if (!assignedTo?.id) return null;
  const fromCrm = users.find(u => u.id === assignedTo.id)?.name?.trim();
  const fromTask = assignedTo.name?.trim();
  const name = fromCrm || fromTask || '';
  if (!name || name.toLowerCase() === 'usuário' || name.toLowerCase() === 'usuario') {
    return 'Sem nome';
  }
  return name.includes('@') ? name.split('@')[0]! : name.split(/\s+/)[0]!;
}

function dealLine(task: Task): string | null {
  const deal = task.lead?.name?.trim();
  const contact = task.lead?.contact?.name?.trim();
  if (!deal && !contact) return null;
  if (contact && deal && (contact === deal || deal.includes(contact))) return deal;
  if (deal && contact) return `${deal} · ${contact}`;
  return deal || contact || null;
}

export function TaskCard({
  task,
  selected,
  onToggleSelect,
  onToggleCompletion,
  onEdit,
  onDelete,
  onDuplicate,
}: TaskCardProps) {
  const { crmUsers } = useUsers();
  const bucket = getTaskDueBucket(task);
  const label = assigneeLabel(task.assignedTo, crmUsers);
  const deal = dealLine(task);
  const type = typeLabel(task.type);
  const selectLabel = `Selecionar ${task.title}`;

  return (
    <li
      className={cn(
        'overflow-hidden rounded-lg border shadow-xs transition-base hover:border-primary/30 hover:shadow-sm',
        'motion-reduce:transition-none',
        'flex items-start md:grid md:grid-cols-[auto_1fr_auto] md:items-center',
        CARD_TONE[bucket],
        task.isCompleted && 'opacity-50',
      )}
    >
      <div
        className={cn(
          'hidden md:flex md:items-center md:justify-center md:gap-s3 md:self-stretch md:border-r md:px-s3 md:py-s3',
          bucket === 'overdue' && 'md:border-error/40',
          bucket === 'today' && 'md:border-warning/40',
        )}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(task.id)}
          aria-label={selectLabel}
          className={SELECT_CLASS}
        />
        <CompletionCheckbox isCompleted={task.isCompleted} onToggle={() => onToggleCompletion(task.id)} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-s1 p-s3 md:px-s4 md:py-s3">
        <header className="flex items-center gap-s3">
          <CompletionCheckbox
            isCompleted={task.isCompleted}
            onToggle={() => onToggleCompletion(task.id)}
            className="shrink-0 md:hidden"
          />
          <h3
            className={cn(
              'm-0 min-w-0 flex-1 truncate text-sm font-medium text-default md:text-base',
              task.isCompleted && 'text-muted line-through',
            )}
          >
            {task.title}
          </h3>
          <div className="shrink-0 md:hidden">
            <TaskActions task={task} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
          </div>
        </header>

        <p className="m-0 flex min-w-0 items-center gap-s2 text-xs">
          <time
            dateTime={task.dueDate}
            className={cn('inline-flex shrink-0 items-center gap-s1 font-mono font-medium md:text-sm', DATE_TONE[bucket])}
          >
            <AppIcon name="calendar" className="size-3 shrink-0 opacity-80" aria-hidden />
            {formatTaskDate(task.dueDate)}
          </time>
          <span className="text-muted md:hidden" aria-hidden>·</span>
          <span className="truncate text-muted md:hidden">{type}</span>
          <span className="badge badge-sm badge-brand hidden md:inline-flex">{type}</span>
          {bucket === 'overdue' && (
            <span className="badge badge-sm badge-danger hidden md:inline-flex">Atrasada</span>
          )}
          {bucket === 'today' && (
            <span className="badge badge-sm badge-warning hidden md:inline-flex">Hoje</span>
          )}
        </p>

        {deal && <p className="m-0 truncate text-sm text-secondary">{deal}</p>}

        <footer className="flex items-center justify-between gap-s2 pt-s2 md:hidden">
          <Assignee label={label} />
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(task.id)}
            aria-label={selectLabel}
            className={SELECT_CLASS}
          />
        </footer>
      </div>

      <div className="hidden items-center gap-s2 px-s4 py-s3 md:flex">
        <Assignee label={label} />
        <TaskActions task={task} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
      </div>
    </li>
  );
}

function Assignee({ label }: { label: string | null }) {
  if (!label) {
    return <span className="text-sm italic text-muted">Sem responsável</span>;
  }
  return (
    <div className="flex min-w-0 items-center gap-s2">
      <span
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-avatar-chart-1-bg text-2xs font-bold text-avatar-chart-1-icon"
        aria-hidden
      >
        {label[0]?.toUpperCase() ?? '?'}
      </span>
      <span className="truncate text-sm font-medium text-secondary">{label}</span>
    </div>
  );
}

function TaskActions({
  task,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  task: Task;
  onEdit: (task: Task) => void;
  onDuplicate: (task: Task) => void;
  onDelete: (task: Task) => void;
}) {
  return (
    <ActionMenu
      actions={[
        createEditAction(() => onEdit(task)),
        createDuplicateAction(() => onDuplicate(task)),
        createDeleteAction(() => onDelete(task)),
      ]}
    />
  );
}
